
import AppProductSkuAutocomplete from "@/components/products/ProductSkuAutocomplete.vue";
import AppStockLocationSelect from "@/components/StockLocationSelect.vue";
import { Contact, Product, ProductStock, SizeTableEntry } from "@/models/base";
import { StockIn } from "@/models/base";
import { BookYearService } from "@/services/book-year-service";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import { ProductStockService } from "@/services/product-stock-service";
import { SizeTableService } from "@/services/size-table-service";
import AppContactSearch from "@/components/contacts/ContactSearchWithBrowse.vue";
import { SupplierService } from "@/services/supplier-service";
import { ProductService } from "@/services/product-service";
import { ProductSupplier } from "@/utils/product-supplier";
import {isChildOf} from "winfakt-vue-components/src/utils/dom";
import { TranslateResult } from "vue-i18n";
import {AuthService, DossierConfigService} from "@/services";
import {MODULE} from "@/config/modules";

export default Vue.extend({
	data() {
		return {
			amount: 1,
			product: null as Product | null,
			supplier: null as Contact | null,
			locationID: 1,
			productDescription: "",
			info: "",
			dateCreated: new Date(),
			buyPrice: 0,
			productStock: undefined as any | ProductStock,
			contactGroupId: 2,
			trackingNumber: "",
			trackingNumberValidUntil: null as null | Date,
			sizeTableEntryID: null as number | null,
			sizeEntries: [] as SizeTableEntry[],
			DecimalSettings: DossierConfigService.getDecimalSettings()
		};
	},
	props: {
		value: Object as () => StockIn,
		single: Boolean
	},
	computed: {
		hasModuleTraceability():boolean {
			return AuthService.hasModule(MODULE.WERKEN_MET_TRACERING);
		},
		productHasSizes():boolean {
			return AuthService.hasModule(MODULE.MATENTABEL) && !!this.product && !!this.product.SizeTableID;
		},
		sizeOptions():ISelectItem<number | null>[] {
			let items:ISelectItem<number | null>[] = [
				{text: this.$ct("no-size").toString(), value: null}
			];
			for (let entry of this.sizeEntries) {
				items.push({text: entry.Name, value: entry.ID});
			}
			return items;
		},
		newStock(): number {
			if (!this.product) {
				return 0;
			}
			return this.productStock ? (this.amount + this.productStock["Location" + (this.locationID) + "Qty"]) : 0;

		},
		maxDate(): Date{
			return new Date(BookYearService.selectedBookYear,11,31);
		},
		contactIconLeft():string{
			if (this.contactGroupId == 1) {
				return "user-tag";
			}else if (this.contactGroupId == 2) {
				return "people-carry";
			}else if (this.contactGroupId == 3) {
				return "id-badge";
			}
			return "";
		},
		contactIconLeftTooltip():TranslateResult{
			if (this.contactGroupId == 1) {
				return this.$ct("search-in-custommers");
			}else if (this.contactGroupId == 2) {
				return this.$ct("search-in-suppliers");
			}else if (this.contactGroupId == 3) {
				return this.$ct("search-in-prospects");
			}
			return this.$ct("common.error");
		},
		showInfoField():boolean{
			return DossierConfigService.getStockLocationConfig().ShowInfoFieldOnStockIn;
		},
		descriptionCols():number{
			if (this.single) {
				return this.showInfoField ? 6 : 10;
			}
			return this.showInfoField ? 4 : 7;
		},
		addDisabled():boolean{
			if (!this.product) return true;
			if (this.product.SerialNumberRequired && !this.trackingNumber.trim()) return true;
			return false;
		},
		searchSupplierLabel():TranslateResult {
			return this.$ct(`common.contact-group.${this.contactGroupId}`);
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(stock: StockIn) {
				if (!stock) return;
				this.amount = stock.Qty || 1;
				let p = new Product();
				p.ID = stock.ProductID;
				this.product = stock.Product || null;
				this.supplier = stock.Supplier || null;
				this.locationID = stock.LocationID;
				this.productDescription = stock.Name;
				this.info = stock.Info || "";
				this.dateCreated = stock.DateCreated;
				this.buyPrice = stock.UnitBuyPrice || 0.0;
				this.trackingNumber = stock.TrackingNumber;
				this.trackingNumberValidUntil = stock.TrackingNumberValidUntil ? new Date(stock.TrackingNumberValidUntil.toJSON()) : null;
				this.sizeTableEntryID = stock.SizeTableEntryID ?? null;
				if(stock.Product){
					this.fetchStock(stock);
				}
			}
		}
	},
	methods: {
		async fetchStock(stock: StockIn){
			if(stock.Product) {
				this.productStock = await ProductStockService.getStocksByIds([stock.Product.StockID]);
				this.product = stock.Product;
				await this.loadSizeEntries();
			}
		},
		async loadSizeEntries() {
			if (!this.product || !this.product.SizeTableID || !AuthService.hasModule(MODULE.MATENTABEL)) {
				this.sizeEntries = [];
				return;
			}
			let table = await SizeTableService.getById(this.product.SizeTableID);
			this.sizeEntries = table.getSortedEntries();
		},
		async add() {
			if (!this.product) {
				return;
			}
			let value = this.value || new StockIn();
			if (value.ID == 0) {
				value.BookYear = BookYearService.selectedBookYear;
			}
			value.Qty = this.amount;
			value.Product = this.product || undefined;
			if (this.product) {
				value.ProductID = this.product.ID;
				value.Sku = this.product.Sku || "";
			}
			value.LocationID = this.locationID;
			value.Name = this.productDescription;
			value.Info = this.info;
			value.DateCreated = this.dateCreated;
			value.UnitBuyPrice = this.buyPrice;
			value.TrackingNumber = this.trackingNumber;
			value.TrackingNumberValidUntil = this.trackingNumberValidUntil;
			value.SizeTableEntryID = this.productHasSizes ? this.sizeTableEntryID : null;
			// Keep the relation in sync so the chosen size name shows immediately (before reload).
			value.SizeTableEntry = this.sizeEntries.find(e => e.ID === value.SizeTableEntryID) ?? null;

			console.log(this.dateCreated);

			await this.checkSupplierAndBuyPrice(value);

			this.$emit("add", value);
			(this.$refs.productSku as any).clear();
		},
		async update() {
			if (!this.product) {
				return;
			}
			let value = this.value || new StockIn();
			if (value.ID == 0) {
				value.BookYear = BookYearService.selectedBookYear;
			}
			value.Qty = this.amount;
			value.Product = this.product || undefined;
			if (this.product) {
				value.ProductID = this.product.ID;
				value.Sku = this.product.Sku || "";
			}
			value.LocationID = this.locationID;
			value.Name = this.productDescription;
			value.Info = this.info;
			value.DateCreated = this.dateCreated;
			value.UnitBuyPrice = this.buyPrice;
			value.TrackingNumber = this.trackingNumber;
			value.TrackingNumberValidUntil = this.trackingNumberValidUntil;
			value.SizeTableEntryID = this.productHasSizes ? this.sizeTableEntryID : null;
			// Keep the relation in sync so the chosen size name shows immediately (before reload).
			value.SizeTableEntry = this.sizeEntries.find(e => e.ID === value.SizeTableEntryID) ?? null;
			this.$emit("update-stock",value);
		},
		async productSelected(product: Product) {
			this.supplier = null;
			this.productStock = await ProductStockService.getStocksByIds([product.StockID]);
			this.buyPrice = product.BuyNettoPrice || 0.0;
			this.sizeTableEntryID = null;
			this.product = product;
			await this.loadSizeEntries();
			// When the product was resolved by scanning a per-size barcode, preselect that size.
			// add() reads this.sizeTableEntryID directly, so setting it here is enough.
			if (product.MatchedSizeTableEntryID && this.sizeEntries.some(e => e.ID === product.MatchedSizeTableEntryID)) {
				this.sizeTableEntryID = product.MatchedSizeTableEntryID;
			}
			if (product.Suppliers.length) {
				let m = product.Suppliers.filter(s => s.IsMainSupplier );
				console.log(m);
				if (m[0].ContactID > 0) {
					this.supplier = await SupplierService.getContact(m[0].ContactID);
				}
				this.buyPrice = m[0].NettoPrice;
			}
			requestAnimationFrame(() => {
				this.productDescription = product.Name;
				(this.$refs.buttonAdd as any).focus();
			});
		},
		async selectSupplier(supplier: Contact) {
			this.supplier = supplier;
			if(!this.product?.Suppliers.length && this.product){
				this.product = await ProductService.getProduct(this.product.ID);
			}
			if (this.product?.Suppliers.length) {
				let m = this.product.Suppliers.filter(s => s.ContactID == supplier.ID );
				if (m[0].ContactID > 0) {
					this.buyPrice = m[0].NettoPrice;
				}
			}
		},
		// eslint-disable-next-line
		async checkSupplierAndBuyPrice(value: StockIn) {
			if (!this.product) {
				return;
			}
			if (this.supplier) {
				let m = this.product.getMainSupplier();
				if (!m || m.ContactID != this.supplier.ID) {
					let s = this.supplier.MainAddress ? this.supplier.MainAddress.CompanyName : "";
					let confirm = await this.$wf.confirm(this.$ct("confirm-change-supplier",
						{supplier: s, name: this.product.Name}),
						this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "info");
					if (confirm) {
						await this.editProductSupplier();
					}
				} else if (m.NettoPrice != this.buyPrice){
					let confirm = await this.$wf.confirm(this.$ct("confirm-edit-buy-price"),
						this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "info");
					if (confirm) {
						await this.editProductSupplier();
					}
				}
				value.Supplier = this.supplier;
				value.SupplierID = this.supplier.ID;
			} else {
				let m = this.product.getMainSupplier();
				if (!m && this.buyPrice>0) {
					//no supplier found, add new buy price
					let confirm = await this.$wf.confirm(this.$ct("confirm-add-buy-price"),
						this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "info");
					if (confirm) {
						await this.addProductBuyPrice();
					}
				}
				if (m && (m.NettoPrice != this.buyPrice)) {
					let confirm = await this.$wf.confirm(this.$ct("confirm-edit-buy-price"),
						this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "info");
					if (confirm) {
						await this.editProductBuyPrice();
					}
				}
			}
		},
		async addProductBuyPrice() {
			if (!this.product)
				return;
			let n = new ProductSupplier({
				ContactID: 0,
				IsMainSupplier: true,
				NettoPrice: this.buyPrice,
			});
			this.product.Suppliers.push(n);
			await ProductService.putProduct(this.product);
		},
		async editProductBuyPrice() {
			if (!this.product)
				return;
			this.product.Suppliers = this.product.Suppliers.map((s) => {
				if(s.IsMainSupplier){
					s.NettoPrice = this.buyPrice;
				}
				return s;
			});
			await ProductService.putProduct(this.product);
		},
		async editProductSupplier() {

			if (!this.supplier || !this.product)
				return;

			let s = this.supplier.MainAddress ? this.supplier.MainAddress.CompanyName : "";

			this.product.Suppliers.forEach(sup => sup.IsMainSupplier = false);

			let found = false;
			this.product.Suppliers = this.product.Suppliers.map((s) => {
				if (this.supplier && s.ContactID == this.supplier.ID) {
					s.IsMainSupplier = true;
					s.NettoPrice = this.buyPrice;
					found = true;
				}
				return s;
			});
			if (!found) {
				let n = new ProductSupplier({
					ContactID: this.supplier.ID,
					IsMainSupplier: true,
					NettoPrice: this.buyPrice,
					SupplierName: s
				});
				this.product.Suppliers.push(n);
			}
			await ProductService.putProduct(this.product);
		},
		onAmountUpdate(){
			this.$emit("amount-update",this.amount);
		},
		onPageDown(event:KeyboardEvent) {
			if (this.$wf.isModalOpen(this)) return;
			let el = this.$el as HTMLElement;
			if (!el) return;
			if (!document.activeElement) return;
			if (!isChildOf(el, document.activeElement as HTMLElement)) return;
			this.add();
		},
		onKeyUp(event:KeyboardEvent) {
			if (event.key == "PageDown") {
				this.onPageDown(event);
			}
		},
		cycleContactGroup(){
			this.contactGroupId++;
			if (this.contactGroupId > 3) {
				this.contactGroupId = 1;
			}
		},
		focusRef(key:string){
			let ref:any = this.$refs[key];
			if (!ref || typeof(ref).focus != "function") return;
			ref.focus();
		}
	},
	mounted() {
		if(this.dateCreated > this.maxDate){
			this.dateCreated = this.maxDate;
		}
	},
	created(){
		window.addEventListener("keyup", this.onKeyUp);
	},
	destroyed(){
		window.removeEventListener("keyup", this.onKeyUp);
	},
	components: {
		AppProductSkuAutocomplete,
		AppStockLocationSelect,
		AppContactSearch
	},
	i18nEx: {
		componentPrefix: "components.stock"
	}
});
